import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Plus, Trash2, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { API } from "../../App";

const STATUS_OPTIONS = [
  { value: "a_faire", label: "À faire", className: "bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300" },
  { value: "en_cours", label: "En cours", className: "bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300" },
  { value: "bloque", label: "Bloqué", className: "bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-300" },
  { value: "validation", label: "Validation", className: "bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300" },
  { value: "termine", label: "Terminé", className: "bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300" },
];

const PRIORITY_OPTIONS = [
  { value: "basse", label: "Basse" },
  { value: "moyenne", label: "Moyenne" },
  { value: "haute", label: "Haute" },
];

function statusMeta(status) {
  return STATUS_OPTIONS.find((s) => s.value === status) || STATUS_OPTIONS[0];
}

export const PhasesAndTasksTab = ({ project }) => {
  const projectId = project.project_id;
  const [phases, setPhases] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState({});
  const [newPhaseTitle, setNewPhaseTitle] = useState("");
  const [newTaskTitleByPhase, setNewTaskTitleByPhase] = useState({});

  const fetchAll = useCallback(async () => {
    try {
      const [phasesRes, tasksRes] = await Promise.all([
        fetch(`${API}/projects/${projectId}/phases`, { credentials: "include" }),
        fetch(`${API}/projects/${projectId}/tasks`, { credentials: "include" }),
      ]);
      if (phasesRes.ok) setPhases(await phasesRes.json());
      if (tasksRes.ok) setTasks(await tasksRes.json());
    } catch (error) {
      console.error("Error fetching phases/tasks:", error);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleAddPhase = async () => {
    if (!newPhaseTitle.trim()) return;
    try {
      const response = await fetch(`${API}/projects/${projectId}/phases`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ title: newPhaseTitle, start_month: 0, duration_months: 1 }),
      });
      if (response.ok) {
        const created = await response.json();
        setPhases((prev) => [...prev, created]);
        setNewPhaseTitle("");
        toast.success("Phase ajoutée");
      } else {
        toast.error("Erreur lors de l'ajout de la phase");
      }
    } catch (error) {
      toast.error("Erreur réseau");
    }
  };

  const handleDeletePhase = async (phaseId) => {
    if (!window.confirm("Supprimer cette phase ? Les tâches associées seront détachées.")) return;
    try {
      const response = await fetch(`${API}/projects/${projectId}/phases/${phaseId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (response.ok) {
        setPhases((prev) => prev.filter((p) => p.phase_id !== phaseId));
        setTasks((prev) => prev.map((t) => (t.phase_id === phaseId ? { ...t, phase_id: null } : t)));
        toast.success("Phase supprimée");
      }
    } catch (error) {
      toast.error("Erreur réseau");
    }
  };

  const handleAddTask = async (phaseId) => {
    const title = (newTaskTitleByPhase[phaseId] || "").trim();
    if (!title) return;
    try {
      const response = await fetch(`${API}/projects/${projectId}/tasks`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ title, phase_id: phaseId, priority: "moyenne" }),
      });
      if (response.ok) {
        const created = await response.json();
        setTasks((prev) => [...prev, created]);
        setNewTaskTitleByPhase((prev) => ({ ...prev, [phaseId]: "" }));
      } else {
        toast.error("Erreur lors de l'ajout de la tâche");
      }
    } catch (error) {
      toast.error("Erreur réseau");
    }
  };

  const handleUpdateTaskStatus = async (taskId, status) => {
    try {
      const response = await fetch(`${API}/projects/${projectId}/tasks/${taskId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ status }),
      });
      if (response.ok) {
        const updated = await response.json();
        setTasks((prev) => prev.map((t) => (t.task_id === taskId ? updated : t)));
      }
    } catch (error) {
      toast.error("Erreur réseau");
    }
  };

  const handleUpdateTaskPriority = async (taskId, priority) => {
    try {
      const response = await fetch(`${API}/projects/${projectId}/tasks/${taskId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ priority }),
      });
      if (response.ok) {
        const updated = await response.json();
        setTasks((prev) => prev.map((t) => (t.task_id === taskId ? updated : t)));
      }
    } catch (error) {
      toast.error("Erreur réseau");
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      const response = await fetch(`${API}/projects/${projectId}/tasks/${taskId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (response.ok) {
        setTasks((prev) => prev.filter((t) => t.task_id !== taskId));
      }
    } catch (error) {
      toast.error("Erreur réseau");
    }
  };

  const toggleCollapse = (phaseId) => {
    setCollapsed((prev) => ({ ...prev, [phaseId]: !prev[phaseId] }));
  };

  const tasksWithoutPhase = tasks.filter((t) => !t.phase_id);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="phases-tasks-tab">
      {phases.map((phase) => {
        const phaseTasks = tasks.filter((t) => t.phase_id === phase.phase_id);
        const isCollapsed = collapsed[phase.phase_id];
        const doneCount = phaseTasks.filter((t) => t.status === "termine").length;

        return (
          <Card key={phase.phase_id}>
            <CardContent className="p-5">
              <div className="flex items-center justify-between gap-3">
                <button
                  onClick={() => toggleCollapse(phase.phase_id)}
                  className="flex items-center gap-2 flex-1 text-left"
                >
                  {isCollapsed ? (
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-slate-400" />
                  )}
                  <span className="font-semibold text-slate-900 dark:text-white" style={{ fontFamily: "Outfit" }}>
                    {phase.title}
                  </span>
                  <span className="text-xs text-slate-400 dark:text-slate-500">
                    {doneCount}/{phaseTasks.length} terminées
                  </span>
                </button>
                <button
                  onClick={() => handleDeletePhase(phase.phase_id)}
                  className="text-slate-400 hover:text-red-500 transition-colors"
                  aria-label="Supprimer la phase"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              {!isCollapsed && (
                <div className="mt-4 space-y-2 pl-6">
                  {phaseTasks.map((task) => {
                    const meta = statusMeta(task.status);
                    return (
                      <div
                        key={task.task_id}
                        className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 dark:border-white/10 p-3"
                      >
                        <span className="flex-1 text-sm text-slate-900 dark:text-white min-w-[140px]">
                          {task.title}
                        </span>

                        <Select value={task.priority} onValueChange={(v) => handleUpdateTaskPriority(task.task_id, v)}>
                          <SelectTrigger className="h-8 w-[110px] text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {PRIORITY_OPTIONS.map((p) => (
                              <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        <Select value={task.status} onValueChange={(v) => handleUpdateTaskStatus(task.task_id, v)}>
                          <SelectTrigger className="h-8 w-[130px] text-xs">
                            <SelectValue>
                              <Badge className={`${meta.className} text-xs`}>{meta.label}</Badge>
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {STATUS_OPTIONS.map((s) => (
                              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        <button
                          onClick={() => handleDeleteTask(task.task_id)}
                          className="text-slate-400 hover:text-red-500 transition-colors"
                          aria-label="Supprimer la tâche"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    );
                  })}

                  <div className="flex gap-2 pt-1">
                    <Input
                      value={newTaskTitleByPhase[phase.phase_id] || ""}
                      onChange={(e) =>
                        setNewTaskTitleByPhase((prev) => ({ ...prev, [phase.phase_id]: e.target.value }))
                      }
                      onKeyDown={(e) => e.key === "Enter" && handleAddTask(phase.phase_id)}
                      placeholder="Nouvelle tâche…"
                      className="h-9 text-sm"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleAddTask(phase.phase_id)}
                      className="h-9 flex-none"
                    >
                      <Plus className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}

      {tasksWithoutPhase.length > 0 && (
        <Card>
          <CardContent className="p-5">
            <p className="font-semibold text-slate-900 dark:text-white" style={{ fontFamily: "Outfit" }}>
              Sans phase
            </p>
            <div className="mt-3 space-y-2">
              {tasksWithoutPhase.map((task) => {
                const meta = statusMeta(task.status);
                return (
                  <div
                    key={task.task_id}
                    className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 dark:border-white/10 p-3"
                  >
                    <span className="flex-1 text-sm text-slate-900 dark:text-white min-w-[140px]">
                      {task.title}
                    </span>
                    <Badge className={`${meta.className} text-xs`}>{meta.label}</Badge>
                    <button
                      onClick={() => handleDeleteTask(task.task_id)}
                      className="text-slate-400 hover:text-red-500 transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex gap-2">
        <Input
          value={newPhaseTitle}
          onChange={(e) => setNewPhaseTitle(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleAddPhase()}
          placeholder="Nouvelle phase…"
        />
        <Button onClick={handleAddPhase} className="bg-blue-600 hover:bg-blue-700 text-white flex-none">
          <Plus className="w-4 h-4 mr-1" />
          Ajouter une phase
        </Button>
      </div>
    </div>
  );
};